import { PropsWithChildren } from 'react';
import { useDidUpdate, usePreservedCallback } from '@toss/react';
import Script from 'next/script';
import { useTranslation } from 'next-i18next';
import '../events.web-zem-charge';
import { resizeZemChargePopupWindow, ZEM_CHARGE_PAYMENT_POPUP_SIZE } from '@zep/common/routes';
import { useDialog } from '@zep/dialog';
import { ZemColorIcon } from '@zep/icons';
import { trackEvent } from '@zep/logger/tracking';
import { useToggle } from '@zep/react';
import { Checkbox, Radio } from '@zep/ui';
import { Colors } from '@zep/ui-tokens';
import { TossPaymentMethod, TossPaymentMethods } from '../../../../../consts';
import { useUnknownErrorDialog } from '../../../../../hooks/useUnknownErrorDialog';
import { API_V2_CASH_BUY_PAYPLE_CALLBACK, API_V2_CASH_BUY_TOSS_CALLBACK_SUCCESS, getCashBuyFailCallbackUrl, useChargeZemInfo } from '../../../../../services';
import { PaypleProvider, usePayple } from '../../../subscription/hooks/usePayple';
import { getZemChargeBenefit } from '../utils/getZemChargeBenefit';
import { buildZemChargeContextProps, buildZemChargeFailureProps, getZemChargeErrorCode } from '../zemChargeAnalytics';
import { ChargeZemStep, useChargeZem } from '../ZemChargeContext';
import { getZemChargePopupContext } from '../zemChargePopupContext';
import { ChargeContainer } from './ChargeContainer';
import S from './PaymentSelectStep.module.scss';
const getZemChargePopupCallbackUrl = (callbackUrl: string) => {
  const popupQuery = getZemChargePopupContext().query;
  const url = new URL(callbackUrl);
  Object.entries(popupQuery).forEach(([key, value]) => {
    url.searchParams.set(key, String(value));
  });
  return url.toString();
};
const resizeZemChargePaymentPopup = (provider: keyof typeof ZEM_CHARGE_PAYMENT_POPUP_SIZE) => {
  if (!getZemChargePopupContext().query.mode) {
    return;
  }
  resizeZemChargePopupWindow(ZEM_CHARGE_PAYMENT_POPUP_SIZE[provider]);
};
export const PaymentSelectStep = () => {
  const {
    currency
  } = useChargeZem();
  return <>
      {currency === 'KRW' ? <TossPaymentSelectedStep /> : <PaypleProvider currency={currency}>
          <PayplePaymentSelectedStep />
        </PaypleProvider>}
    </>;
};
const PayplePaymentSelectedStep = () => {
  const {
    showWidget
  } = usePayple();
  const {
    selectedZemInfo
  } = useChargeZem();
  const onPayment = (orderId: string) => {
    resizeZemChargePaymentPopup('payple');
    showWidget({
      type: 'chargeZem',
      comments: `${selectedZemInfo.productName}`,
      orderId,
      totalAmount: selectedZemInfo.price,
      callbackUrl: getZemChargePopupCallbackUrl(API_V2_CASH_BUY_PAYPLE_CALLBACK)
    });
  };
  return <Container onPayment={onPayment} data-sentry-element="Container" data-sentry-component="PayplePaymentSelectedStep" data-sentry-source-file="PaymentSelectStep.tsx" />;
};
const TossPaymentSelectedStep = () => {
  const dialog = useDialog();
  const {
    paymentMethod,
    selectedZemInfo,
    step,
    zem
  } = useChargeZem();
  const submitPayment = (orderId: string) => {
    resizeZemChargePaymentPopup('toss');
    window.TossPayments(process.env.NEXT_PUBLIC_TOSS_CLIENT_KEY_FOR_ZEM).requestPayment(paymentMethod, {
      amount: selectedZemInfo.price,
      orderId,
      orderName: selectedZemInfo.productName,
      windowTarget: 'self',
      successUrl: getZemChargePopupCallbackUrl(API_V2_CASH_BUY_TOSS_CALLBACK_SUCCESS),
      failUrl: getCashBuyFailCallbackUrl({
        paymentMethod,
        step,
        zem,
        ...getZemChargePopupContext().query
      }),
      useInternationalCardOnly: false
    }).catch((error: unknown) => {
      const chargeContext = getZemChargePopupContext();
      trackEvent('web_zem_charge_error', buildZemChargeFailureProps({
        code: getZemChargeErrorCode(error, 'PG_REQUEST_FAILED'),
        context: chargeContext,
        stage: 'pg_request'
      }));
      dialog.alert({
        title: '',
        content: String(error)
      });
    });
  };
  return <>
      <Script id={'tosspayments'} async src={'https://js.tosspayments.com/v1'} data-sentry-element="Script" data-sentry-source-file="PaymentSelectStep.tsx" />
      <Container onPayment={submitPayment} data-sentry-element="Container" data-sentry-source-file="PaymentSelectStep.tsx" />
    </>;
};
const Container = (props: {
  onPayment: (orderId: string) => void;
}) => {
  const {
    handleStep,
    selectedZemInfo,
    currency,
    paymentMethod
  } = useChargeZem();
  const {
    t
  } = useTranslation(['common', 'manage']);
  const {
    mutateAsync
  } = useChargeZemInfo();
  const showErrorDialog = useUnknownErrorDialog();
  const generateOrderNumber = async () => {
    try {
      const {
        data: {
          orderId
        }
      } = await mutateAsync({
        id: selectedZemInfo.id
      });
      const chargeContext = getZemChargePopupContext();
      const benefit = getZemChargeBenefit({
        baseZem: selectedZemInfo.addCashMoney,
        bonusZem: selectedZemInfo.addCashMoneyPromotion,
        totalZem: selectedZemInfo.totalCashMoney,
        promotionType: selectedZemInfo.promotionType
      });
      trackEvent('charge_zem_payment', {
        zem_amount: selectedZemInfo.addCashMoney,
        bonus_zem: benefit.bonusZem,
        total_zem: benefit.totalZem,
        promotion_type: benefit.promotionType,
        payment_amount: selectedZemInfo.price,
        currency,
        method: paymentMethod,
        ...buildZemChargeContextProps(chargeContext)
      });
      props.onPayment(orderId);
      return;
    } catch (e) {
      const chargeContext = getZemChargePopupContext();
      trackEvent('web_zem_charge_error', buildZemChargeFailureProps({
        code: getZemChargeErrorCode(e, 'ORDER_CREATE_FAILED'),
        context: chargeContext,
        stage: 'order_create'
      }));
      showErrorDialog(e);
    }
  };
  return <ChargeContainer title={t('manage:charge.paymentSelect.paymentMethod')} backStep={() => {
    handleStep(ChargeZemStep.Zem);
  }} mainAction={generateOrderNumber} content={<Content />} data-sentry-element="ChargeContainer" data-sentry-component="Container" data-sentry-source-file="PaymentSelectStep.tsx" />;
};
const Content = () => {
  return <div className={S.content} data-sentry-component="Content" data-sentry-source-file="PaymentSelectStep.tsx">
      <ContentHeader data-sentry-element="ContentHeader" data-sentry-source-file="PaymentSelectStep.tsx" />
      <Divider data-sentry-element="Divider" data-sentry-source-file="PaymentSelectStep.tsx" />
      <Payments data-sentry-element="Payments" data-sentry-source-file="PaymentSelectStep.tsx" />
      <ContentSummary data-sentry-element="ContentSummary" data-sentry-source-file="PaymentSelectStep.tsx" />
    </div>;
};
const Payments = () => {
  const {
    currency,
    paymentMethod,
    handlePaymentMethod
  } = useChargeZem();
  const {
    t
  } = useTranslation(['manage']);
  return <div className={S.payment} data-sentry-component="Payments" data-sentry-source-file="PaymentSelectStep.tsx">
      <div className={S.payments_title}>
        {t('manage:charge.paymentSelect.paymentMethod')}
      </div>
      <div className={S.payments_methods}>
        {currency === 'KRW' ? TossPaymentMethods.map(({
        method,
        labelKey
      }) => <Radio isFullWidth label={t(labelKey)} key={method} checked={paymentMethod === method} onChange={() => handlePaymentMethod(method)} labelPlacement={'left'} />) : <Radio isFullWidth label={'paypleCard'} defaultChecked labelPlacement={'left'} />}
      </div>
      {currency === 'KRW' && paymentMethod === TossPaymentMethod.Card && <TossCardDescription />}
      {currency === 'KRW' && paymentMethod === TossPaymentMethod.Transfer && <TossAccountTransferDescription />}
      {currency !== 'KRW' && <PayplePaymentNotice />}
    </div>;
};
const DescriptionWrapper = (props: PropsWithChildren) => {
  return <ul className={S.payment_description} data-sentry-component="DescriptionWrapper" data-sentry-source-file="PaymentSelectStep.tsx">{props.children}</ul>;
};
const TossCardDescription = () => {
  const {
    t
  } = useTranslation(['manage']);
  return <DescriptionWrapper data-sentry-element="DescriptionWrapper" data-sentry-component="TossCardDescription" data-sentry-source-file="PaymentSelectStep.tsx">
      <li>{t('manage:charge.tossPaymentCard.description1')}</li>
      <li>{t('manage:charge.tossPaymentCard.description7')}</li>
      <li>{t('manage:charge.tossPaymentCard.description2')}</li>
      <li>{t('manage:charge.tossPaymentCard.description3')}</li>
      <li>{t('manage:charge.tossPaymentCard.description4')}</li>
      <li>{t('manage:charge.tossPaymentCard.description5')}</li>
      <li>{t('manage:charge.tossPaymentCard.description6')}</li>
    </DescriptionWrapper>;
};
const TossAccountTransferDescription = () => {
  const {
    t
  } = useTranslation(['manage']);
  return <DescriptionWrapper data-sentry-element="DescriptionWrapper" data-sentry-component="TossAccountTransferDescription" data-sentry-source-file="PaymentSelectStep.tsx">
      <li>{t('manage:donate_payment_banktransfer_description1')}</li>
      <li>{t('manage:donate_payment_banktransfer_description2')}</li>
    </DescriptionWrapper>;
};
const PayplePaymentNotice = () => {
  const {
    t
  } = useTranslation(['manage']);
  return <DescriptionWrapper data-sentry-element="DescriptionWrapper" data-sentry-component="PayplePaymentNotice" data-sentry-source-file="PaymentSelectStep.tsx">
      <li>{t('manage:charge.payplePaymentCard.description1')}</li>
      <li>{t('manage:charge.payplePaymentCard.description2')}</li>
      <li>{t('manage:charge.payplePaymentCard.description3')}</li>
    </DescriptionWrapper>;
};
const ContentHeader = () => {
  const {
    selectedZemInfo
  } = useChargeZem();
  const {
    t
  } = useTranslation(['manage']);
  return <div className={S.header} data-sentry-component="ContentHeader" data-sentry-source-file="PaymentSelectStep.tsx">
      <div className={S.header_title}>{t('manage:donate_order_product')}</div>
      <div className={S.header_row}>
        <div className={S.header_row_item}>
          <div>{t('manage:donate_product_name')}</div>
          <div className={S.header_zem}>
            <ChargeZemIcon data-sentry-element="ChargeZemIcon" data-sentry-source-file="PaymentSelectStep.tsx" />
            {selectedZemInfo.addCashMoney} ZEM
          </div>
        </div>
        <div className={S.header_row_item}>
          <div>{t('manage:donate_payment_price')}</div>
          <div className={S.primary_color}>{selectedZemInfo.localeString}</div>
        </div>
      </div>
    </div>;
};
const Divider = () => {
  return <div style={{
    width: '100%',
    height: '1px',
    backgroundColor: Colors.Gray300
  }} data-sentry-component="Divider" data-sentry-source-file="PaymentSelectStep.tsx" />;
};
const ContentSummary = () => {
  const [isAgree, setAgree] = useToggle(false);
  const {
    t
  } = useTranslation(['manage']);
  const {
    selectedZemInfo,
    setPaymentButtonState
  } = useChargeZem();
  const toggle = usePreservedCallback(_isAgree => {
    setPaymentButtonState(_isAgree);
  });
  useDidUpdate(() => {
    toggle(isAgree);
  }, [toggle, isAgree]);
  return <div className={S.summary} data-sentry-component="ContentSummary" data-sentry-source-file="PaymentSelectStep.tsx">
      <div className={S.summary_title}>
        <span>{t('manage:donate_final_payment')}:</span>
        <span className={S.summary_title_price}>
          {selectedZemInfo.localeString}
        </span>
      </div>
      <Checkbox circle checked={isAgree} onChange={setAgree} label={<span className={S.check_box_lable}>
            {t('manage:donate_payment_check_amount')}
          </span>} data-sentry-element="Checkbox" data-sentry-source-file="PaymentSelectStep.tsx" />
    </div>;
};
export const ChargeZemIcon = () => {
  return <div className={S.header_zem_frame} data-sentry-component="ChargeZemIcon" data-sentry-source-file="PaymentSelectStep.tsx">
      <ZemColorIcon width={20} height={20} data-sentry-element="ZemColorIcon" data-sentry-source-file="PaymentSelectStep.tsx" />
    </div>;
};